import React from "react";
import { MenuType } from "@/types/leader-board";

interface LeaderboardSkeletonProps {
  className?: string;
  selectedMenu?: MenuType;
}

export default function LeaderboardSkeleton({ 
  className = "", 
  selectedMenu = "users" 
}: LeaderboardSkeletonProps) {
  const renderAvatarSkeleton = () => {
    if (selectedMenu === "collections") {
      return (
        <div className="relative w-full aspect-square">
          <div className="w-full h-full bg-gray-700 rounded animate-pulse" />
          {/* Corner squares for collections */}
          <div className="absolute top-0 left-0 w-[5%] h-[5%] bg-gray-600 rounded-sm" />
          <div className="absolute top-0 right-0 w-[5%] h-[5%] bg-gray-600 rounded-sm" />
          <div className="absolute bottom-0 left-0 w-[5%] h-[5%] bg-gray-600 rounded-sm" />
          <div className="absolute bottom-0 right-0 w-[5%] h-[5%] bg-gray-600 rounded-sm" />
        </div>
      );
    }
    
    // Users - circular avatar
    return <div className="w-full h-full bg-gray-700 rounded-full animate-pulse" />;
  };

  return (
    <div className={`w-full flex flex-col items-center mt-[30px] relative ${className}`}>

      {/* Top position skeleton */}
      <div className="w-[35%] flex flex-col items-center">
        <div className="relative w-full aspect-square mb-4">
          {renderAvatarSkeleton()}
        </div>
        <div className="w-3/4 h-6 bg-gray-700 rounded animate-pulse mb-2" />
        <div className="w-1/2 h-4 bg-gray-700 rounded animate-pulse mb-1" />
        <div className="w-1/3 h-4 bg-gray-700 rounded animate-pulse" />
      </div>

      {/* Second and third positions skeleton */}
      <div className="w-full flex justify-center gap-[40px] pt-[26px]">
        <div className="w-[35%] flex flex-col items-center">
          <div className="relative w-full aspect-square mb-4">
            {renderAvatarSkeleton()}
          </div>
          <div className="w-3/4 h-6 bg-gray-700 rounded animate-pulse mb-2" />
          <div className="w-1/2 h-4 bg-gray-700 rounded animate-pulse mb-1" />
          <div className="w-1/3 h-4 bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="w-[35%] flex flex-col items-center">
          <div className="relative w-full aspect-square mb-4">
            {renderAvatarSkeleton()}
          </div>
          <div className="w-3/4 h-6 bg-gray-700 rounded animate-pulse mb-2" />
          <div className="w-1/2 h-4 bg-gray-700 rounded animate-pulse mb-1" />
          <div className="w-1/3 h-4 bg-gray-700 rounded animate-pulse" />
        </div>
      </div>

      {/* My data section skeleton */}
      <div className="w-full pt-[20px]">
        <div className="bg-primary-blue px-5 py-2 w-full relative flex items-center justify-between">
          <div className="flex items-center gap-[15px]">
            <div className="relative w-[66px] h-[66px]">
              {selectedMenu === "collections" ? (
                <div className="w-full h-full bg-gray-700 rounded animate-pulse" />
              ) : (
                <div className="w-full h-full bg-gray-700 rounded-full animate-pulse" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <div className="w-16 h-5 bg-gray-700 rounded animate-pulse" />
              <div className="w-8 h-5 bg-gray-700 rounded animate-pulse" />
              <div className="w-32 h-4 bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 bg-gray-700 rounded animate-pulse" />
            <div className="w-12 h-4 bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
      </div>

      {/* List items skeleton */}
      {Array.from({ length: 5 }, (_, index) => (
        <div
          key={index}
          className="w-full flex items-center justify-between px-[20px] py-[15px]"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <div className="flex items-center gap-[15px]">
            <div className="relative">
              <div className="w-[66px] h-[66px] opacity-50">
                {selectedMenu === "collections" ? (
                  <div className="w-full h-full bg-gray-700 rounded animate-pulse" />
                ) : (
                  <div className="w-full h-full bg-gray-700 rounded-full animate-pulse" />
                )}
              </div>
              <div className="absolute top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%]">
                <div className="w-6 h-5 bg-gray-600 rounded animate-pulse" />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="w-32 h-5 bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-16 h-5 bg-gray-700 rounded animate-pulse" />
            <div className="w-6 h-6 bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}
